import { Link } from 'react-router-dom';
import {
  Badge,
  EmptyState,
  ErrorBanner,
  Money,
  Skeleton,
  Stat,
  useApiQuery,
  type ScreenContext,
} from '../../engine';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { formatDateTime, formatMoney, formatQty } from '../../lib/format';

// Ledger account detail header + entries tab. The account is append-only and immutable, so
// there is no editable overview (builtinTabs: []); the generic detail screen still gives the
// `verify` meta action (proves the hash chain end to end). The header shows the stored
// balance and the balance resolved as of now; the tab is the read-only entry chain.

interface Entry {
  id: string;
  account_seq: number;
  occurred_at: string;
  entry_type: string;
  reverses_entry_id: string | null;
  amount_minor: string;
  currency: string;
  quantity: string | number | null;
  memo: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
}

export function LedgerHeader({ id, row }: ScreenContext) {
  const balance = useApiQuery<{ balance_minor: string }>(['ledger-balance', id], `/v1/ledger-accounts/${id}/balance`, { enabled: Boolean(id) });
  if (!row) return null;
  const currency = String(row.currency ?? 'USD');
  return (
    <div className="hf-stats" aria-label="Ledger account summary">
      <Stat
        label="Running balance"
        value={<Money minor={row.running_amount_minor as string} currency={currency} color />}
        sub={balance.data ? `As of now: ${formatMoney(balance.data.balance_minor, currency)}` : undefined}
        icon="book-open"
        accent
      />
      <Stat label="Quantity" value={<span className="hf-num">{formatQty(row.running_quantity as string)}</span>} icon="hash" />
      <Stat label="Entries" value={<span className="hf-num">{String(row.last_seq ?? 0)}</span>} sub="seq 1 → last, contiguous" icon="list" />
    </div>
  );
}

export function LedgerEntriesTab({ id }: ScreenContext) {
  const entriesQ = useApiQuery<{ data: Entry[] }>(['ledger-entries', id], `/v1/ledger-accounts/${id}/entries`, { enabled: Boolean(id) });

  const columns: GridColumn<Entry>[] = [
    { id: 'account_seq', header: 'Seq', width: 70, align: 'right', cell: (e) => <span className="hf-mono hf-muted">#{e.account_seq}</span> },
    { id: 'occurred_at', header: 'When', width: 170, cell: (e) => <span className="hf-num">{formatDateTime(e.occurred_at)}</span> },
    { id: 'entry_type', header: 'Type', cell: (e) => (
      <span className="hf-row" style={{ gap: 'var(--s-1)' }}>
        <Badge tone="muted" dot={false}>{e.entry_type}</Badge>
        {e.reverses_entry_id && <Badge tone="warn" dot={false}>reversal</Badge>}
      </span>
    ) },
    { id: 'amount_minor', header: 'Amount', align: 'right', cell: (e) => <Money minor={e.amount_minor} currency={e.currency} color /> },
    { id: 'quantity', header: 'Qty', align: 'right', cell: (e) => <span className="hf-num">{formatQty(e.quantity)}</span> },
    { id: 'memo', header: 'Memo', cell: (e) => <span className="hf-muted">{e.memo ?? '—'}</span> },
    { id: 'invoice', header: 'Ref', cell: (e) => (e.invoice_id ? <Link className="hf-mono hf-link" to={`/invoices/${e.invoice_id}`}>{e.invoice_number ?? `#${e.invoice_id.slice(0, 8)}`}</Link> : <span className="hf-muted">—</span>) },
  ];

  if (entriesQ.isLoading) return <Skeleton lines={4} />;
  if (entriesQ.error) return <ErrorBanner error={entriesQ.error} />;

  return (
    <DataGrid<Entry>
      aria-label="Ledger entries"
      columns={columns}
      rows={entriesQ.data?.data ?? []}
      keyOf={(e) => e.id}
      hideToolbar
      empty={<EmptyState title="No entries yet" hint="This account has no posted ledger entries." icon="book-open" />}
    />
  );
}
